import axios from "axios";

export const DIAL_RESULT_LOOKUP_ATTEMPTS = 6;
export const DIAL_RESULT_LOOKUP_DELAY_MS = 500;

const STATUSES_THAT_MAY_GET_ERROR_CODE = new Set(["failed", "canceled"]);

// Normal call outcomes, not Dial failures. Twilio never attaches an error code to them.
const UNANSWERED_STATUSES = new Set(["busy", "no-answer"]);

function dialStatus(dialResult) {
  return String(dialResult?.status || "").toLowerCase();
}

/**
 * "busy" or "no-answer" when the recipient never picked up, otherwise null.
 *
 * @param {{ status?: string|null } | null | undefined} dialResult
 * @returns {"busy" | "no-answer" | null}
 */
export function getUnansweredOutcome(dialResult) {
  const status = dialStatus(dialResult);
  return UNANSWERED_STATUSES.has(status) ? status : null;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function hasErrorCode(dialResult) {
  return dialResult?.errorCode != null && dialResult.errorCode !== "";
}

/**
 * Twilio often marks the child `failed` before `error_code` is written.
 * Keep polling until we have a code, or the status is not one that gets a code.
 *
 * @param {{ errorCode?: number|string|null, status?: string|null } | null | undefined} dialResult
 * @returns {boolean}
 */
export function isDialResultReady(dialResult) {
  if (!dialResult) return false;
  if (hasErrorCode(dialResult)) return true;
  if (getUnansweredOutcome(dialResult)) return true;

  if (STATUSES_THAT_MAY_GET_ERROR_CODE.has(dialStatus(dialResult))) return false;

  return true;
}

/**
 * Completed child with no errorCode is a normal short call (recipient hung up),
 * and busy / no-answer are normal unanswered calls; neither is a Dial failure.
 * Empty lookup still counts as an error so we keep the generic fallback when
 * Twilio has not written anything yet.
 *
 * @param {{ errorCode?: number|string|null, status?: string|null } | null | undefined} dialResult
 * @returns {boolean}
 */
export function shouldShowDialResultError(dialResult) {
  if (getUnansweredOutcome(dialResult)) return false;
  if (hasErrorCode(dialResult)) return true;
  if (!dialResult) return true;

  const status = dialStatus(dialResult);
  if (status === "completed" || status === "answered") return false;
  return true;
}

/**
 * @param {{ organizationId: string, callSid: string }} params
 * @returns {Promise<{ parentCallSid?: string, children?: object[], dialResult?: object|null }>}
 */
export async function fetchDialResult({ organizationId, callSid }) {
  const res = await axios.post(
    process.env.REACT_APP_BACKEND_URL + "twilio/lookup-dial-result",
    {
      organization_id: organizationId,
      call_sid: callSid,
    }
  );
  return res.data;
}

function shouldRetryLookupError(error) {
  const status = error?.response?.status;
  if (status === 400 || status === 401 || status === 403) return false;
  return true;
}

/**
 * Retry while the child call is missing, or is failed/canceled without an
 * errorCode yet. Does not poll on the server.
 *
 * @param {{
 *   organizationId: string,
 *   callSid: string,
 *   attempts?: number,
 *   delayMs?: number,
 *   wait?: (ms: number) => Promise<void>,
 * }} params
 */
export async function fetchDialResultWithRetry({
  organizationId,
  callSid,
  attempts = DIAL_RESULT_LOOKUP_ATTEMPTS,
  delayMs = DIAL_RESULT_LOOKUP_DELAY_MS,
  wait = sleep,
}) {
  let last = null;

  for (let i = 0; i < attempts; i++) {
    try {
      last = await fetchDialResult({ organizationId, callSid });
      if (isDialResultReady(last?.dialResult)) return last;
    } catch (error) {
      if (!shouldRetryLookupError(error)) throw error;
      last = { dialResult: null, children: [], lookupError: error };
    }

    if (i < attempts - 1) {
      await wait(delayMs);
    }
  }

  return last;
}
